import React, {useCallback, useMemo, useRef, useState} from 'react';
import _ from 'lodash';
import CONFIG from '../config.js';
import {useUser} from './user.js';
import {useLiveRef, useObjectMemo} from './react_hooks.js';
import {useWebSocket} from './websocket.js';
import storage from './storage.js';

const JobsWebSocketContext = React.createContext({
    subscribe: _.noop,
    unsubscribe: _.noop,
});

export const useJobsWebSocket = ()=>{
    let ctx = useLiveRef(React.useContext(JobsWebSocketContext));
    return useCallback((jobId, cb)=>{
        ctx.current.subscribe(jobId, cb);
        return ()=>ctx.current.unsubscribe(jobId, cb);
    }, []);
};

export const JobsWebSocketProvider = props=>{
    let {children} = props;
    let [user] = useUser();
    let url = useMemo(()=>user&&
        `${CONFIG.WS_URL}jobs?userId=${user.id}&token=${storage.get('sc_token')}`);
    let [hasListeners, setHasListeners] = useState();
    let enable = useMemo(()=>!!(user&&url&&hasListeners),
        [user, url, hasListeners]);
    let initialized = useRef();
    let listeners = useRef({}); // jobId => [cb]
    let onClose = useCallback(()=>{ initialized.current = false; }, []);
    let actionMap = useMemo(()=>({
        initialized: ws=>{
            initialized.current = true;
            _.keys(listeners.current).forEach(jobId=>ws.sendEvent('subscribe', {jobId}));
        },
        event: (__, data)=>{
            let {jobId, action, payload} = data;
            listeners.current[jobId]?.forEach(cb=>cb(action, payload));
        },
    }), []);
    let {sendEvent} = useWebSocket({url, enable, onClose, actionMap});
    let subscribe = useCallback((jobId, cb)=>{
        setHasListeners(true);
        if (!listeners.current[jobId]) {
            if (initialized.current)
                sendEvent('subscribe', {jobId});
            listeners.current[jobId] = [cb];
            return;
        }
        listeners.current[jobId].push(cb);
    }, [sendEvent]);
    let unsubscribe = useCallback((jobId, cb)=>{
        listeners.current[jobId] = listeners.current[jobId].filter(_cb=>cb!==_cb);
        if (!listeners.current[jobId].length) {
            delete listeners.current[jobId];
            sendEvent('unsubscribe', {jobId});
        }
        if (_.isEmpty(listeners.current))
            setHasListeners(false);
    }, [sendEvent]);
    let ctx = useObjectMemo({subscribe, unsubscribe});
    return <JobsWebSocketContext.Provider value={ctx}>
      {children}
    </JobsWebSocketContext.Provider>
};